import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { TextInput } from './Field';

interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
  requireText?: string;
  requireTextLabel?: ReactNode;
}
type Confirm = (options: ConfirmOptions) => Promise<boolean>;
const ConfirmContext = createContext<Confirm | undefined>(undefined);
interface ActiveState extends ConfirmOptions { resolve: (value: boolean) => void; }

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ActiveState | null>(null);
  const [text, setText] = useState('');
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<Confirm>((options) => {
    setText('');
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setActive({ ...options, resolve });
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setActive(null);
  }, []);

  const requireText = active?.requireText?.trim();
  // Case and spaces don't count, as on a plate. The field only DISPLAYS capitals
  // (CSS uppercase), so typed "testdel5" looked right and the button stayed off
  // while a pasted plate worked (Bobby, 14 Oct 2026).
  const forComparison = (value: string) => value.replace(/\s+/g, '').toUpperCase();
  const textOk = !requireText || forComparison(text) === forComparison(requireText);
  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Modal
        open={!!active}
        onClose={() => settle(false)}
        title={active?.title}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => settle(false)}>
              {active?.cancelLabel ?? 'Cancel'}
            </Button>
            {/* Destructive actions are outlined, never filled — the one exception is a
                permanent deletion, which is the case that asks you to type the plate. */}
            <Button
              variant={active?.tone === 'danger' ? (requireText ? 'danger' : 'danger-outline') : 'primary'}
              disabled={!textOk}
              onClick={() => settle(true)}
              data-autofocus
            >
              {active?.confirmLabel ?? 'Confirm'}
            </Button>
          </>
        }
      >
        {active?.message && <div className="text-[15px] leading-relaxed text-ink2">{active.message}</div>}
        {requireText && (
          <div className="mt-4">
            <label htmlFor="confirm-text" className="eyebrow mb-[7px] block">
              {active?.requireTextLabel ?? (
                <>Type <span className="reg normal-case tracking-[0.04em] text-ink">{requireText}</span> to confirm</>
              )}
            </label>
            <TextInput
              id="confirm-text"
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && textOk) settle(true); }}
              className="font-mono uppercase tracking-[0.04em]"
            />
          </div>
        )}
      </Modal>
    </ConfirmContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useConfirm(): Confirm {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}
